// ═══════════════════════════════════════════════════════════════════
//  searchEngine — Pure substring search over SearchableEntity[].
// ═══════════════════════════════════════════════════════════════════
//
//  - For queries < 2 chars: fall back to skip (returns []).
//  - For queries 2-3 chars: linear scan over fields.
//  - For queries ≥ 4 chars: try inverted-index prefix match first,
//    then verify with a substring check (handles infix matches).
//
//  Index size is capped at MAX_INDEX_SIZE to keep large workspaces
//  responsive — past that, a banner can warn the user.
//
//  An LRU cache holds the last 50 query results for 30s.
//
// ═══════════════════════════════════════════════════════════════════

import type {
  SearchHit,
  SearchHitMatch,
  SearchQueryOpts,
  SearchableEntity,
} from '@/types/search';
import { fieldWeight } from './indexEntities';

export const MAX_INDEX_SIZE = 50_000;

// ────────────────────────────────────────────────────────────────────
//  Inverted index
// ────────────────────────────────────────────────────────────────────

export interface InvertedIndex {
  /** Word-prefix → entity-id set. */
  byPrefix: Map<string, Set<string>>;
  /** id → entity for O(1) lookup during query. */
  byId: Map<string, SearchableEntity>;
  /** Total entities indexed (capped at MAX_INDEX_SIZE). */
  size: number;
  /** True when input exceeded MAX_INDEX_SIZE. */
  truncated: boolean;
}

const PREFIX_LENGTHS = [2, 3];

export function buildInvertedIndex(
  entities: readonly SearchableEntity[],
): InvertedIndex {
  const truncated = entities.length > MAX_INDEX_SIZE;
  const used = truncated ? entities.slice(0, MAX_INDEX_SIZE) : entities;
  const byPrefix = new Map<string, Set<string>>();
  const byId = new Map<string, SearchableEntity>();
  for (const e of used) {
    byId.set(e.id, e);
    for (const value of Object.values(e.fields)) {
      addValue(byPrefix, value, e.id);
    }
  }
  return { byPrefix, byId, size: used.length, truncated };
}

function addValue(
  byPrefix: Map<string, Set<string>>,
  value: string,
  entityId: string,
): void {
  if (typeof value !== 'string' || !value) return;
  const lower = value.toLowerCase();
  // Tokenize by whitespace + common punctuation so we get word-prefixes.
  const tokens = lower.split(/[\s,;:.\-/_()[\]{}]+/).filter(Boolean);
  for (const tok of tokens) {
    for (const len of PREFIX_LENGTHS) {
      if (tok.length < len) continue;
      const prefix = tok.slice(0, len);
      if (!byPrefix.has(prefix)) byPrefix.set(prefix, new Set());
      byPrefix.get(prefix)!.add(entityId);
    }
  }
}

// ────────────────────────────────────────────────────────────────────
//  LRU cache
// ────────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 30_000;
const CACHE_MAX = 50;

interface CacheEntry {
  hits: SearchHit[];
  ts: number;
}

const queryCache = new Map<string, CacheEntry>();

function cacheKey(opts: SearchQueryOpts): string {
  return JSON.stringify({
    q: opts.query,
    s: opts.scope,
    sf: opts.sourceIdFilter ?? '',
    tf: opts.sourceTypeFilter ?? null,
    cs: !!opts.caseSensitive,
    re: !!opts.regex,
    l: opts.limit ?? 200,
  });
}

export function clearSearchCache(): void {
  queryCache.clear();
}

// ────────────────────────────────────────────────────────────────────
//  runParsedSearch — apply ParsedQuery clauses on top of runSearch
// ────────────────────────────────────────────────────────────────────

import type { ParsedQuery } from './parseQuery';

export function runParsedSearch(
  index: InvertedIndex,
  parsed: ParsedQuery,
  baseOpts: Omit<SearchQueryOpts, 'query'>,
): SearchHit[] {
  if (!parsed.hasContent) return [];

  // Build a seed from the most distinctive clause for the prefix-index pass.
  const seed =
    parsed.phrases[0] ??
    parsed.positiveTerms[0] ??
    parsed.fieldFilters[0]?.value ??
    parsed.typeFilter ??
    '';
  if (!seed) return [];

  const opts: SearchQueryOpts = {
    ...baseOpts,
    query: seed,
    sourceTypeFilter: parsed.sourceTypeFilter ?? baseOpts.sourceTypeFilter,
    sourceIdFilter:
      parsed.sourceFilter?.kind === 'specific'
        ? parsed.sourceFilter.sourceId
        : baseOpts.sourceIdFilter,
    scope:
      parsed.sourceFilter?.kind === 'workflows'
        ? 'workflows'
        : parsed.sourceFilter?.kind === 'atlas'
          ? 'atlas'
          : baseOpts.scope,
    // Larger seed so post-filters have headroom.
    limit: Math.max(baseOpts.limit ?? 200, 500),
  };
  const seedHits = runSearch(index, opts);

  const finalLimit = baseOpts.limit ?? 200;
  const out: SearchHit[] = [];
  for (const hit of seedHits) {
    if (out.length >= finalLimit) break;
    if (!matchesParsed(hit, parsed)) continue;
    out.push(hit);
  }
  return out;
}

function matchesParsed(hit: SearchHit, parsed: ParsedQuery): boolean {
  // All positive terms must appear in some field.
  for (const term of parsed.positiveTerms) {
    if (!fieldsContain(hit, term)) return false;
  }
  // All phrases must appear (literal substring).
  for (const phrase of parsed.phrases) {
    if (!fieldsContain(hit, phrase.toLowerCase())) return false;
  }
  // No negative term may appear.
  for (const neg of parsed.negativeTerms) {
    if (fieldsContain(hit, neg)) return false;
  }
  // Field filters: at least one of each filter must match its named field.
  for (const ff of parsed.fieldFilters) {
    const value = hit.fields[ff.field];
    if (typeof value !== 'string') return false;
    if (!value.toLowerCase().includes(ff.value.toLowerCase())) return false;
  }
  // Type filter — workflow-block / atlas-* entities expose `type` field.
  if (parsed.typeFilter) {
    const t = hit.fields.type ?? hit.fields.t;
    if (!t || !t.toLowerCase().includes(parsed.typeFilter)) return false;
  }
  return true;
}

function fieldsContain(hit: SearchHit, needle: string): boolean {
  for (const v of Object.values(hit.fields)) {
    if (v.toLowerCase().includes(needle)) return true;
  }
  return false;
}

// ────────────────────────────────────────────────────────────────────
//  runSearch
// ────────────────────────────────────────────────────────────────────

export function runSearch(
  index: InvertedIndex,
  opts: SearchQueryOpts,
): SearchHit[] {
  const q = opts.query.trim();
  if (!q || q.length < 2) return [];

  const key = cacheKey(opts);
  const cached = queryCache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.hits;

  const limit = opts.limit ?? 200;
  const cs = !!opts.caseSensitive;
  const needle = cs ? q : q.toLowerCase();

  // Candidate set
  let candidates: Iterable<SearchableEntity>;
  if (q.length >= 4) {
    const prefix = needle.slice(0, 3);
    const byPrefix = index.byPrefix.get(prefix);
    if (!byPrefix || byPrefix.size === 0) {
      // No prefix match → still try fuzzy substring scan, but bounded.
      candidates = bounded(index.byId.values(), 5_000);
    } else {
      candidates = mapIds(byPrefix, index.byId);
    }
  } else {
    // Short query — must scan.
    candidates = bounded(index.byId.values(), 5_000);
  }

  const hits: SearchHit[] = [];
  for (const e of candidates) {
    if (hits.length >= limit) break;
    if (!entityPasses(e, opts)) continue;
    const matches = collectMatches(e, needle, cs);
    if (matches.length === 0) continue;
    const totalScore = matches.reduce((s, m) => s + m.score, 0) + e.score * 0.1;
    hits.push({ ...e, matches, totalScore });
  }
  hits.sort((a, b) => b.totalScore - a.totalScore || a.entityLabel.localeCompare(b.entityLabel));

  // Bound and cache
  evictExpired();
  if (queryCache.size >= CACHE_MAX) {
    const firstKey = queryCache.keys().next().value;
    if (firstKey) queryCache.delete(firstKey);
  }
  queryCache.set(key, { hits, ts: Date.now() });
  return hits;
}

function entityPasses(e: SearchableEntity, opts: SearchQueryOpts): boolean {
  if (opts.sourceIdFilter && e.sourceId !== opts.sourceIdFilter) return false;
  if (opts.sourceTypeFilter && !opts.sourceTypeFilter.includes(e.sourceType)) return false;
  if (opts.scope === 'workflows' && !e.sourceType.startsWith('workflow-')) return false;
  if (opts.scope === 'atlas' && !e.sourceType.startsWith('atlas-')) return false;
  return true;
}

function collectMatches(
  e: SearchableEntity,
  needle: string,
  cs: boolean,
): SearchHitMatch[] {
  const matches: SearchHitMatch[] = [];
  for (const [field, value] of Object.entries(e.fields)) {
    const haystack = cs ? value : value.toLowerCase();
    const idx = haystack.indexOf(needle);
    if (idx === -1) continue;
    const w = fieldWeight(field);
    matches.push({
      field,
      snippet: trim(value, idx, needle.length),
      score: w,
    });
  }
  return matches;
}

function trim(s: string, idx: number, qLen: number): string {
  const start = Math.max(0, idx - 30);
  const end = Math.min(s.length, idx + qLen + 30);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < s.length ? '…' : '';
  return prefix + s.slice(start, end) + suffix;
}

function* bounded<T>(it: Iterable<T>, n: number): Iterable<T> {
  let i = 0;
  for (const v of it) {
    if (i++ >= n) return;
    yield v;
  }
}

function* mapIds(
  ids: Set<string>,
  byId: Map<string, SearchableEntity>,
): Iterable<SearchableEntity> {
  for (const id of ids) {
    const e = byId.get(id);
    if (e) yield e;
  }
}

function evictExpired(): void {
  const now = Date.now();
  for (const [k, v] of queryCache) {
    if (now - v.ts >= CACHE_TTL_MS) queryCache.delete(k);
  }
}
